import React, { useState, useRef, useEffect } from "react";
import { Button } from "../../Button";

interface PartnerAccountActionsPopoverProps {
  onResendInvitation: () => void;
  onRemoveAccess: () => void;
  isPending?: boolean;
  onSendManually?: () => void;
}

const PartnerAccountActionsPopover: React.FC<PartnerAccountActionsPopoverProps> = ({
  onResendInvitation,
  onRemoveAccess,
  isPending,
  onSendManually,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0 });
  const popoverRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPopoverPosition({
        top: rect.bottom + 8,
        left: rect.right - 192, // 192px = 12rem (w-48)
      });
    }
  }, [isOpen]);

  const handleAction = (action: () => void) => {
    action();
    setIsOpen(false);
  };

  return (
    <>
      <div ref={buttonRef}>
        <Button
          variant="icon"
          onClick={() => setIsOpen(!isOpen)}
          className="p-2"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="currentColor"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="8" cy="3" r="1.5" />
            <circle cx="8" cy="8" r="1.5" />
            <circle cx="8" cy="13" r="1.5" />
          </svg>
        </Button>
      </div>

      {isOpen && (
        <div
          ref={popoverRef}
          className="fixed w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-[9999]"
          style={{
            top: `${popoverPosition.top}px`,
            left: `${popoverPosition.left}px`,
          }}
        >
          <button
            onClick={() => handleAction(onResendInvitation)}
            className="w-full text-left px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm"
          >
            Resend Invitation
          </button>
          {isPending && onSendManually && (
            <button
              onClick={() => handleAction(onSendManually)}
              className="w-full text-left px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm"
            >
              Send Manually
            </button>
          )}
          <button
            onClick={() => handleAction(onRemoveAccess)}
            className="w-full text-left px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm text-red-600"
          >
            Remove Access
          </button>
        </div>
      )}
    </>
  );
};

export default PartnerAccountActionsPopover;
